import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from './useAuth';
import { useData } from './useData';
import { useUI } from '../providers/MoleUIContext';
import { periodRequestConfig } from './useDateRange';

export const useDailyAggregateData = (storageKey: string, endpoint: string) => {
	const { api } = useAuth();
	const { queueReRun, t } = useUI();
	const {
		data,
		lastUpdate,
		loadFromIndexedDB,
		saveFromServer,
		dateRange,
		setDateRange,
		loading,
		setLoading,
	} = useData(storageKey);

	const toastIdRef = useRef<string | number>(-1);
	const fetchGenRef = useRef(0);
	const initializedRef = useRef(false);
	const lastFetchedPeriodRef = useRef<string | null>(null);

	const periodKey = useMemo(
		() => `${dateRange?.from?.toISOString() ?? ''}|${dateRange?.to?.toISOString() ?? ''}`,
		[dateRange?.from, dateRange?.to],
	);

	const fetchData = useCallback(async (useLoadModal = true) => {
		const gen = ++fetchGenRef.current;
		setLoading(true);
		try {
			const res = await api.get(endpoint, {
				...(periodRequestConfig(dateRange)),
			});

			if (gen !== fetchGenRef.current) return;

			if (res?.status === 200) {
				saveFromServer(res.data, dateRange);
				lastFetchedPeriodRef.current = periodKey;
				toast.update(toastIdRef.current, {
					render: t('common:toast.success.refresh'),
					type: 'success',
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRun(() => fetchData(useLoadModal));
			} else if (res) {
				console.error('Failed to fetch data:', res);
				toast.dismiss(toastIdRef.current);
			} else {
				toast.dismiss(toastIdRef.current);
			}
		} finally {
			if (gen === fetchGenRef.current) {
				setLoading(false);
			}
		}
	}, [api, dateRange, endpoint, periodKey, queueReRun, saveFromServer, setLoading, t]);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			const loaded = await loadFromIndexedDB();
			if (cancelled) return;
			initializedRef.current = true;
			if (loaded) return;
			await fetchData(false);
			if (!cancelled) {
				lastFetchedPeriodRef.current = periodKey;
			}
		})();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!initializedRef.current || lastFetchedPeriodRef.current === periodKey) return;

		if (lastFetchedPeriodRef.current === null && data.length > 0) {
			lastFetchedPeriodRef.current = periodKey;
			return;
		}

		void fetchData(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [periodKey]);

	return {
		data,
		lastUpdate,
		dateRange,
		setDateRange,
		loading,
		fetchData,
	};
};